import { phone } from 'phone';
import React, { forwardRef, useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { Input } from '../Input';
import { PhoneInputStyled } from './PhoneInputStyled';
import { type PhoneNumberFieldProps } from './PhoneNumberField.types';

export function validatePhoneNumber(
  phoneNumber: string,
  required: boolean,
  disableValidation: boolean,
) {
  if (!phoneNumber) {
    return { isValid: !required };
  }
  if (disableValidation) {
    return { isValid: true };
  }
  const { isValid, countryIso2 } = phone(phoneNumber, { validateMobilePrefix: false });
  return { isValid, country: countryIso2 };
}

const PhoneNumberField: FC<PhoneNumberFieldProps> = ({
  colorMode,
  dataTestId = 'text-field',
  disabled,
  labelText,
  hintText,
  inline = false,
  placeholder,
  hasError = false,
  errorText,
  required = false,
  phoneNumber,
  updatePhoneNumber,
  retrieveError,
  showLabelAsterisk,
  preferredCountries,
  onlyCountries,
  warnWhenNotInPreferredCountriesMessage,
  errorWhenNotInOnlyCountriesMessage,
  disableValidation = false,
  enableLongNumbers = false,
}) => {
  const [phoneNumberErrorText, setPhoneNumberErrorText] = useState<string | undefined>('');
  useEffect(() => {
    const { isValid } = validatePhoneNumber(phoneNumber, required, disableValidation);
    let error: string | undefined = '';
    if (phoneNumber && (hasError || !isValid)) {
      error = errorText;
    }
    setPhoneNumberErrorText(error);
    if (retrieveError && error) {
      retrieveError(error);
    }
  }, [errorText, hasError, phoneNumber, retrieveError, required, disableValidation]);

  const clearPhoneNumber = useCallback(
    () =>
      updatePhoneNumber?.('', {
        valid: required ? false : true,
      }),
    [updatePhoneNumber, required],
  );

  const onUpdate = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value ? `+${event.target.value}` : event.target.value;
      updatePhoneNumber?.(value, {
        valid: validatePhoneNumber(value, required, disableValidation).isValid,
      });
    },
    [updatePhoneNumber, required, disableValidation],
  );

  const warningText = useMemo(() => {
    const { country } = validatePhoneNumber(phoneNumber, required, disableValidation);
    if (!country) return;
    if (!preferredCountries?.includes(country.toLowerCase()))
      return warnWhenNotInPreferredCountriesMessage;
  }, [
    phoneNumber,
    preferredCountries,
    warnWhenNotInPreferredCountriesMessage,
    required,
    disableValidation,
  ]);

  const onlyCountryErrorText = useMemo(() => {
    const { country } = validatePhoneNumber(phoneNumber, required, disableValidation);
    if (!country) return;
    if (!onlyCountries?.includes(country.toLowerCase())) return errorWhenNotInOnlyCountriesMessage;
  }, [phoneNumber, onlyCountries, errorWhenNotInOnlyCountriesMessage, required, disableValidation]);

  return (
    <Input
      autoComplete="nope" // To actually stop browser's auto suggest with placeholder saying 'phone number'
      className="input"
      placeholder={placeholder}
      colorMode={colorMode}
      disabled={disabled}
      labelText={labelText}
      hintText={hintText}
      name="phone-number"
      onChange={onUpdate}
      onClear={clearPhoneNumber}
      errorText={phoneNumberErrorText || onlyCountryErrorText}
      warningText={warningText}
      value={phoneNumber}
      required={required}
      type="text"
      inline={inline}
      data-testid={dataTestId}
      showLabelAsterisk={showLabelAsterisk}
      inputComponent={useMemo(
        () =>
          // eslint-disable-next-line react/display-name
          forwardRef(
            ({ value, placeholder, className, onChange, disabled, ...props }: any, ref) => {
              // While this might seem like a wild guess, setting the country in the phone input selector can significantly
              // enhance the user experience by making it more intuitive and reducing the effort needed to select the correct country.
              const countryGuess = useMemo(() => {
                const [_, country = ''] = navigator.language.split('-');
                return country.toLowerCase();
              }, []);
              return (
                <PhoneInputStyled
                  disabled={disabled}
                  colorMode={colorMode}
                  value={value}
                  placeholder={placeholder}
                  inputProps={{
                    ref,
                    ...props,
                    onFocus: undefined,
                  }}
                  inputClass={className}
                  onChange={(value: string) => onChange({ target: { value } })}
                  country={countryGuess}
                  preferredCountries={preferredCountries}
                  onlyCountries={onlyCountries}
                  enableLongNumbers={enableLongNumbers}
                />
              );
            },
          ),
        [
          colorMode,
          enableLongNumbers,
          JSON.stringify(preferredCountries),
          JSON.stringify(onlyCountries),
        ],
      )}
    />
  );
};

export default PhoneNumberField;
